import React, { useState } from 'react';
import './AiCommentary.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AiCommentary = ({ which, period, extraQuery }) => {
  const [text, setText] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cached, setCached] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const fetchOnce = async (force) => {
    const extra = extraQuery ? `&${extraQuery}` : '';
    const res = await fetch(
      `${API_URL}/api/backtest/commentary?which=${which}&period=${period}&force=${force}${extra}`
    );
    return res.json();
  };

  const generate = async (force = false) => {
    setLoading(true);
    setError(null);
    setRetrying(false);
    try {
      const data = await fetchOnce(force);
      if (data.status === 'success') {
        setText(data.commentary);
        setCached(!!data.cached);
        return;
      }
      setError(data.message || 'Failed to generate commentary');
    } catch (err) {
      // Likely the backend completed but a proxy timed the response out.
      // Wait briefly and retry once against the cache (force=false).
      setRetrying(true);
      try {
        await new Promise((r) => setTimeout(r, 2000));
        const data2 = await fetchOnce(false);
        if (data2.status === 'success') {
          setText(data2.commentary);
          setCached(!!data2.cached);
          setError(null);
          return;
        }
        setError(data2.message || 'Connection error — please try again');
      } catch (err2) {
        setError('Connection error — please try again');
      } finally {
        setRetrying(false);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ai-commentary" data-testid={`backtest${which}-ai-commentary`}>
      <div className="ai-commentary-header">
        <div className="ai-commentary-title">
          <span className="ai-badge">AI</span>
          <span>Portfolio Analyst Commentary</span>
        </div>
        {!text && !loading && (
          <button
            onClick={() => generate(false)}
            className="ai-commentary-btn"
            data-testid={`backtest${which}-ai-generate-btn`}
          >
            Generate Commentary
          </button>
        )}
        {text && !loading && (
          <button
            onClick={() => generate(true)}
            className="ai-commentary-btn ai-commentary-btn-secondary"
            data-testid={`backtest${which}-ai-regenerate-btn`}
          >
            Regenerate
          </button>
        )}
      </div>

      {loading && (
        <div className="ai-commentary-loading" data-testid={`backtest${which}-ai-loading`}>
          <div className="ai-commentary-dots"><span></span><span></span><span></span></div>
          <span>{retrying ? 'Loading from cache…' : 'Analyzing trades and macro signals...'}</span>
        </div>
      )}

      {error && <div className="ai-commentary-error" data-testid={`backtest${which}-ai-error`}>{error}</div>}

      {text && !loading && (
        <div className="ai-commentary-body" data-testid={`backtest${which}-ai-text`}>
          {text}
          {cached && <span className="ai-commentary-cached" title="Cached result">cached</span>}
        </div>
      )}

      {!text && !loading && !error && (
        <div className="ai-commentary-placeholder">
          Click <strong>Generate Commentary</strong> to read a 2-3 sentence narrative on this period's
          performance, recent rotations, and current positioning.
        </div>
      )}
    </div>
  );
};

export default AiCommentary;
